import {mapObject} from '@benchling/js-utils';

import {Equation} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/FlatEquationSolver';
import PreciseDecimal from 'util/precise-decimal/PreciseDecimal';

export enum StoichiometryVariable {
  LIMITING_REACTANT_EQUIVALENCE = 'LIMITING_REACTANT_EQUIVALENCE',
  LIMITING_REACTANT_MOLS = 'LIMITING_REACTANT_MOLS',
  LIMITING_REACTANT_MOL_UNIT_CONVERSION = 'LIMITING_REACTANT_MOL_UNIT_CONVERSION',
  EQUIVALENCE = 'EQUIVALENCE',
  MOLECULE_PURITY = 'MOLECULE_PURITY',
  FORMULA_WEIGHT = 'FORMULA_WEIGHT',
  MOLS = 'MOLS',
  MOL_UNIT_CONVERSION = 'MOL_UNIT_CONVERSION',
  MASS = 'MASS',
  MASS_UNIT_CONVERSION = 'MASS_UNIT_CONVERSION',
  VOLUME = 'VOLUME',
  VOLUME_UNIT_CONVERSION = 'VOLUME_UNIT_CONVERSION',
  DENSITY = 'DENSITY',
  DENSITY_UNIT_CONVERSION = 'DENSITY_UNIT_CONVERSION',
  MOLARITY = 'MOLARITY',
  MOLARITY_UNIT_CONVERSION = 'MOLARITY_UNIT_CONVERSION',
}

const VOLUME_VARIABLES = [
  StoichiometryVariable.VOLUME,
  StoichiometryVariable.VOLUME_UNIT_CONVERSION,
  StoichiometryVariable.MOLECULE_PURITY,
];
const MASS_VARIABLES = [
  StoichiometryVariable.MASS,
  StoichiometryVariable.MASS_UNIT_CONVERSION,
  StoichiometryVariable.MOLECULE_PURITY,
];
const MOLS_VARIABLES = [StoichiometryVariable.MOLS, StoichiometryVariable.MOL_UNIT_CONVERSION];
const DENSITY_VARIABLES = [StoichiometryVariable.DENSITY, StoichiometryVariable.DENSITY_UNIT_CONVERSION];
const MOLARITY_VARIABLES = [StoichiometryVariable.MOLARITY, StoichiometryVariable.MOLARITY_UNIT_CONVERSION];
const LIMITING_REACTANT_MOLS_VARIABLES = [
  StoichiometryVariable.LIMITING_REACTANT_MOLS,
  StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION,
];

export const LIMITING_REACTANT_VARIABLES = new Set([
  StoichiometryVariable.LIMITING_REACTANT_EQUIVALENCE,
  StoichiometryVariable.LIMITING_REACTANT_MOLS,
  StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION,
]);

function convertPreciseDecimalArgsToFloats<TVariable extends StoichiometryVariable>(
  args: Record<TVariable, PreciseDecimal>
): Record<TVariable, number> {
  // Keep the ?. to safely return undefined for missing values. We already assert that all needed values are
  // in args.
  return mapObject(args, (value) => value?.toFloat());
}

/** A small helper to wrap equations to take in and return PreciseDecimal values, but actually operate on
 * float numbers.
 *
 * Eventually, we should move this to use PreciseDecimal.decimal for all calculations for better precision.
 * */
function makePreciseDecimalWrappedEquation<TVariable extends StoichiometryVariable>(
  equation: Equation<TVariable, number>
): Equation<TVariable, PreciseDecimal> {
  return {
    ...equation,
    calc: (args) => PreciseDecimal.of(equation.calc(convertPreciseDecimalArgsToFloats(args))),
  };
}

const EQUIVALENCE_FROM_MOLS_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.EQUIVALENCE,
    deps: [...MOLS_VARIABLES, ...LIMITING_REACTANT_MOLS_VARIABLES],
    calc: (args) => {
      const {molsInMols, limitingReactantMolsInMols} = getValuesInBaseUnits(args);
      return molsInMols / limitingReactantMolsInMols;
    },
  });

const EQUIVALENCE_FROM_MASS_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.EQUIVALENCE,
    deps: [...MASS_VARIABLES, ...LIMITING_REACTANT_MOLS_VARIABLES, StoichiometryVariable.FORMULA_WEIGHT],
    calc: (args) => {
      const {[StoichiometryVariable.FORMULA_WEIGHT]: molecularWeight} = args;
      const {pureMassInGrams, limitingReactantMolsInMols} = getValuesInBaseUnits(args);
      return pureMassInGrams / (molecularWeight * limitingReactantMolsInMols);
    },
  });

const EQUIVALENCE_FROM_VOLUME_DENSITY_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.EQUIVALENCE,
    deps: [
      ...VOLUME_VARIABLES,
      ...DENSITY_VARIABLES,
      ...LIMITING_REACTANT_MOLS_VARIABLES,
      StoichiometryVariable.FORMULA_WEIGHT,
    ],
    calc: (args) => {
      const {[StoichiometryVariable.FORMULA_WEIGHT]: molecularWeight} = args;
      const {pureVolumeInLiters, densityInGramsPerLiter, limitingReactantMolsInMols} =
        getValuesInBaseUnits(args);
      return (pureVolumeInLiters * densityInGramsPerLiter) / molecularWeight / limitingReactantMolsInMols;
    },
  });

const EQUIVALENCE_FROM_VOLUME_MOLARITY_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.EQUIVALENCE,
    deps: [...MOLARITY_VARIABLES, ...VOLUME_VARIABLES, ...LIMITING_REACTANT_MOLS_VARIABLES],
    calc: (args) => {
      const {pureVolumeInLiters, molarityInMolsPerLiter, limitingReactantMolsInMols} =
        getValuesInBaseUnits(args);
      return (pureVolumeInLiters * molarityInMolsPerLiter) / limitingReactantMolsInMols;
    },
  });

const MOLS_FROM_MASS_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.MOLS,
    deps: [
      ...MASS_VARIABLES,
      StoichiometryVariable.FORMULA_WEIGHT,
      StoichiometryVariable.MOL_UNIT_CONVERSION,
    ],
    calc: (args) => {
      const {
        [StoichiometryVariable.FORMULA_WEIGHT]: molecularWeight,
        [StoichiometryVariable.MOL_UNIT_CONVERSION]: molUnitConversion,
      } = args;
      const {pureMassInGrams} = getValuesInBaseUnits(args);
      const molsInMols = pureMassInGrams / molecularWeight;
      return molsInMols / molUnitConversion;
    },
  });

const MOLS_FROM_EQUIVALENCE_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.MOLS,
    deps: [
      ...LIMITING_REACTANT_MOLS_VARIABLES,
      StoichiometryVariable.EQUIVALENCE,
      StoichiometryVariable.MOL_UNIT_CONVERSION,
    ],
    calc: (args) => {
      const {
        [StoichiometryVariable.EQUIVALENCE]: equivalence,
        [StoichiometryVariable.MOL_UNIT_CONVERSION]: molUnitConversion,
      } = args;
      const {limitingReactantMolsInMols} = getValuesInBaseUnits(args);
      const molsInMols = equivalence * limitingReactantMolsInMols;
      return molsInMols / molUnitConversion;
    },
  });

const MOLS_FROM_VOLUME_DENSITY_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.MOLS,
    deps: [
      ...VOLUME_VARIABLES,
      ...DENSITY_VARIABLES,
      StoichiometryVariable.FORMULA_WEIGHT,
      StoichiometryVariable.MOL_UNIT_CONVERSION,
    ],
    calc: (args) => {
      const {
        [StoichiometryVariable.FORMULA_WEIGHT]: molecularWeight,
        [StoichiometryVariable.MOL_UNIT_CONVERSION]: molUnitConversion,
      } = args;
      const {pureVolumeInLiters, densityInGramsPerLiter} = getValuesInBaseUnits(args);
      const molsInMols = (pureVolumeInLiters * densityInGramsPerLiter) / molecularWeight;
      return molsInMols / molUnitConversion;
    },
  });

const MOLS_FROM_VOLUME_MOLARITY_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.MOLS,
    deps: [...VOLUME_VARIABLES, ...MOLARITY_VARIABLES, StoichiometryVariable.MOL_UNIT_CONVERSION],
    calc: (args) => {
      const {[StoichiometryVariable.MOL_UNIT_CONVERSION]: molUnitConversion} = args;
      const {pureVolumeInLiters, molarityInMolsPerLiter} = getValuesInBaseUnits(args);
      const molsInMols = pureVolumeInLiters * molarityInMolsPerLiter;
      return molsInMols / molUnitConversion;
    },
  });

const MASS_FROM_MOLS_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.MASS,
    deps: [
      ...MOLS_VARIABLES,
      StoichiometryVariable.FORMULA_WEIGHT,
      StoichiometryVariable.MASS_UNIT_CONVERSION,
      StoichiometryVariable.MOLECULE_PURITY,
    ],
    calc: (args) => {
      const {
        [StoichiometryVariable.FORMULA_WEIGHT]: molecularWeight,
        [StoichiometryVariable.MASS_UNIT_CONVERSION]: massUnitConversion,
        [StoichiometryVariable.MOLECULE_PURITY]: moleculePurity,
      } = args;
      const {molsInMols} = getValuesInBaseUnits(args);
      const pureMassInGrams = molsInMols * molecularWeight;
      return pureMassInGrams / massUnitConversion / moleculePurity;
    },
  });

const MASS_FROM_EQUIVALENCE_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.MASS,
    deps: [
      ...LIMITING_REACTANT_MOLS_VARIABLES,
      StoichiometryVariable.EQUIVALENCE,
      StoichiometryVariable.FORMULA_WEIGHT,
      StoichiometryVariable.MASS_UNIT_CONVERSION,
      StoichiometryVariable.MOLECULE_PURITY,
    ],
    calc: (args) => {
      const {
        [StoichiometryVariable.EQUIVALENCE]: equivalence,
        [StoichiometryVariable.FORMULA_WEIGHT]: molecularWeight,
        [StoichiometryVariable.MASS_UNIT_CONVERSION]: massUnitConversion,
        [StoichiometryVariable.MOLECULE_PURITY]: moleculePurity,
      } = args;
      const {limitingReactantMolsInMols} = getValuesInBaseUnits(args);
      const pureMassInGrams = equivalence * molecularWeight * limitingReactantMolsInMols;
      return pureMassInGrams / massUnitConversion / moleculePurity;
    },
  });

const VOLUME_FROM_MOLS_DENSITY_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.VOLUME,
    deps: [
      ...MOLS_VARIABLES,
      ...DENSITY_VARIABLES,
      StoichiometryVariable.VOLUME_UNIT_CONVERSION,
      StoichiometryVariable.FORMULA_WEIGHT,
      StoichiometryVariable.MOLECULE_PURITY,
    ],
    calc: (args) => {
      const {
        [StoichiometryVariable.VOLUME_UNIT_CONVERSION]: volumeUnitConversion,
        [StoichiometryVariable.FORMULA_WEIGHT]: molecularWeight,
        [StoichiometryVariable.MOLECULE_PURITY]: moleculePurity,
      } = args;
      const {molsInMols, densityInGramsPerLiter} = getValuesInBaseUnits(args);
      const pureVolumeInLiters = (molsInMols * molecularWeight) / densityInGramsPerLiter;
      return pureVolumeInLiters / volumeUnitConversion / moleculePurity;
    },
  });

const VOLUME_FROM_MOLS_MOLARITY_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.VOLUME,
    deps: [
      ...MOLARITY_VARIABLES,
      ...MOLS_VARIABLES,
      StoichiometryVariable.VOLUME_UNIT_CONVERSION,
      StoichiometryVariable.MOLECULE_PURITY,
    ],
    calc: (args) => {
      const {
        [StoichiometryVariable.VOLUME_UNIT_CONVERSION]: volumeUnitConversion,
        [StoichiometryVariable.MOLECULE_PURITY]: moleculePurity,
      } = args;
      const {molsInMols, molarityInMolsPerLiter} = getValuesInBaseUnits(args);
      const pureVolumeInLiters = molsInMols / molarityInMolsPerLiter;
      return pureVolumeInLiters / volumeUnitConversion / moleculePurity;
    },
  });

const VOLUME_FROM_EQUIVALENCE_DENSITY_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.VOLUME,
    deps: [
      ...DENSITY_VARIABLES,
      ...LIMITING_REACTANT_MOLS_VARIABLES,
      StoichiometryVariable.FORMULA_WEIGHT,
      StoichiometryVariable.EQUIVALENCE,
      StoichiometryVariable.VOLUME_UNIT_CONVERSION,
      StoichiometryVariable.MOLECULE_PURITY,
    ],
    calc: (args) => {
      const {
        [StoichiometryVariable.FORMULA_WEIGHT]: molecularWeight,
        [StoichiometryVariable.EQUIVALENCE]: equivalence,
        [StoichiometryVariable.VOLUME_UNIT_CONVERSION]: volumeUnitConversion,
        [StoichiometryVariable.MOLECULE_PURITY]: moleculePurity,
      } = args;
      const {densityInGramsPerLiter, limitingReactantMolsInMols} = getValuesInBaseUnits(args);
      return (
        (equivalence * limitingReactantMolsInMols * molecularWeight) /
        densityInGramsPerLiter /
        volumeUnitConversion /
        moleculePurity
      );
    },
  });

const VOLUME_FROM_EQUIVALENCE_MOLARITY_EQUATION: Equation<StoichiometryVariable, PreciseDecimal> =
  makePreciseDecimalWrappedEquation({
    solution: StoichiometryVariable.VOLUME,
    deps: [
      ...MOLARITY_VARIABLES,
      ...LIMITING_REACTANT_MOLS_VARIABLES,
      StoichiometryVariable.EQUIVALENCE,
      StoichiometryVariable.VOLUME_UNIT_CONVERSION,
      StoichiometryVariable.MOLECULE_PURITY,
    ],
    calc: (args) => {
      const {
        [StoichiometryVariable.EQUIVALENCE]: equivalence,
        [StoichiometryVariable.VOLUME_UNIT_CONVERSION]: volumeUnitConversion,
        [StoichiometryVariable.MOLECULE_PURITY]: moleculePurity,
      } = args;
      const {molarityInMolsPerLiter, limitingReactantMolsInMols} = getValuesInBaseUnits(args);
      return (
        (equivalence * limitingReactantMolsInMols) /
        molarityInMolsPerLiter /
        volumeUnitConversion /
        moleculePurity
      );
    },
  });

export const STOICHIOMETRY_CALCULATION_EQUATIONS: Array<Equation<StoichiometryVariable, PreciseDecimal>> = [
  EQUIVALENCE_FROM_MOLS_EQUATION,
  EQUIVALENCE_FROM_MASS_EQUATION,
  EQUIVALENCE_FROM_VOLUME_DENSITY_EQUATION,
  EQUIVALENCE_FROM_VOLUME_MOLARITY_EQUATION,
  MOLS_FROM_MASS_EQUATION,
  MOLS_FROM_EQUIVALENCE_EQUATION,
  MOLS_FROM_VOLUME_DENSITY_EQUATION,
  MOLS_FROM_VOLUME_MOLARITY_EQUATION,
  MASS_FROM_MOLS_EQUATION,
  MASS_FROM_EQUIVALENCE_EQUATION,
  VOLUME_FROM_MOLS_DENSITY_EQUATION,
  VOLUME_FROM_MOLS_MOLARITY_EQUATION,
  VOLUME_FROM_EQUIVALENCE_DENSITY_EQUATION,
  VOLUME_FROM_EQUIVALENCE_MOLARITY_EQUATION,
];

interface ValuesInBaseUnits {
  limitingReactantMolsInMols: number;
  molsInMols: number;
  pureMassInGrams: number;
  pureVolumeInLiters: number;
  densityInGramsPerLiter: number;
  molarityInMolsPerLiter: number;
}

/** Convert values with their unit conversions to the "base" units. */
function getValuesInBaseUnits(args: Record<StoichiometryVariable, number>): ValuesInBaseUnits {
  const {
    [StoichiometryVariable.LIMITING_REACTANT_MOLS]: limitingReactantMols,
    [StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION]: limitingReactantMolUnitConversion,
    [StoichiometryVariable.MOLS]: mols,
    [StoichiometryVariable.MOL_UNIT_CONVERSION]: molUnitConversion,
    [StoichiometryVariable.MASS]: mass,
    [StoichiometryVariable.MASS_UNIT_CONVERSION]: massUnitConversion,
    [StoichiometryVariable.VOLUME]: volume,
    [StoichiometryVariable.VOLUME_UNIT_CONVERSION]: volumeUnitConversion,
    [StoichiometryVariable.DENSITY]: density,
    [StoichiometryVariable.DENSITY_UNIT_CONVERSION]: densityUnitConversion,
    [StoichiometryVariable.MOLARITY]: molarity,
    [StoichiometryVariable.MOLARITY_UNIT_CONVERSION]: molarityUnitConversion,
    [StoichiometryVariable.MOLECULE_PURITY]: moleculePurity,
  } = args;
  return {
    molsInMols: mols * molUnitConversion,
    limitingReactantMolsInMols: limitingReactantMols * limitingReactantMolUnitConversion,
    pureMassInGrams: mass * massUnitConversion * moleculePurity,
    pureVolumeInLiters: volume * volumeUnitConversion * moleculePurity,
    densityInGramsPerLiter: density * densityUnitConversion,
    molarityInMolsPerLiter: molarity * molarityUnitConversion,
  };
}
